import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { cmsAdminApi } from "../../api/cmsApi";

export default function AdminDashboard() {
  const [blogs, setBlogs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [contactInquiries, setContactInquiries] = useState([]);
  const [saleInquiries, setSaleInquiries] = useState([]);
  const [awsActivate, setAwsActivate] = useState([]);
  const [partners, setPartners] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [blogData, jobData, appData, contactData, inquiryData, partnerData, awsData] = await Promise.all([
          cmsAdminApi.listBlogs(),
          cmsAdminApi.listJobs(),
          cmsAdminApi.listJobApplications(),
          cmsAdminApi.listContactInquiries(),
          cmsAdminApi.listSaleInquiries(),
          cmsAdminApi.listPartners(),
          cmsAdminApi.listAwsActivate(),
        ]);
        setBlogs(blogData);
        setJobs(jobData);
        setApplications(appData);
        setContactInquiries(contactData);
        setSaleInquiries(inquiryData);
        setPartners(partnerData);
        setAwsActivate(awsData);
        setError("");
      } catch (e) {
        setError(e?.message || "Could not load dashboard data");
      }
    })();
  }, []);

  const recentBlogs = blogs.slice(0, 3);
  const recentJobs = jobs.slice(0, 3);
  const recentApps = applications.slice(0, 4);
  const recentContacts = contactInquiries.slice(0, 4);

  const stats = useMemo(
    () => ({
      blogs: blogs.length,
      partners: partners.length,
      openJobs: jobs.filter((j) => j.status === "open").length,
      applications: applications.length,
      contactInquiries: contactInquiries.length,
      saleInquiries: saleInquiries.length,
      awsActivate: awsActivate.length,
      newApplications: applications.filter((a) => a.status === "new").length,
      newContacts: contactInquiries.filter((a) => a.status === "new").length,
      newAws: awsActivate.filter((a) => a.status === "new").length,
    }),
    [blogs, jobs, applications, contactInquiries, saleInquiries, partners, awsActivate]
  );

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>Dashboard</h1>
          <p>Overview of content, careers, and form submissions.</p>
        </div>
      </header>
      {error ? <p className="text-danger mb-3">{error}</p> : null}

      <div className="admin-stat-grid">
        <div className="admin-stat-card">
          <span>Blogs</span>
          <strong>{stats.blogs}</strong>
          <Link to="/admin/blogs">Manage</Link>
        </div>
        <div className="admin-stat-card">
          <span>Partners</span>
          <strong>{stats.partners}</strong>
          <Link to="/admin/partners">Manage</Link>
        </div>
        <div className="admin-stat-card">
          <span>Open Jobs</span>
          <strong>{stats.openJobs}</strong>
          <Link to="/admin/jobs">Manage</Link>
        </div>
        <div className="admin-stat-card">
          <span>Applications</span>
          <strong>{stats.applications}</strong>
          <Link to="/admin/applications">Review</Link>
        </div>
        <div className="admin-stat-card admin-stat-card--accent">
          <span>Contact Inquiries</span>
          <strong>{stats.contactInquiries}</strong>
          <Link to="/admin/contact-inquiries">View messages{stats.newContacts ? ` (${stats.newContacts} new)` : ""}</Link>
        </div>
        <div className="admin-stat-card admin-stat-card--accent">
          <span>Sale Inquiries</span>
          <strong>{stats.saleInquiries}</strong>
          <Link to="/admin/sale-inquiries">View leads</Link>
        </div>
        <div className="admin-stat-card admin-stat-card--accent">
          <span>AWS Activate</span>
          <strong>{stats.awsActivate}</strong>
          <Link to="/admin/aws-activate">View apps{stats.newAws ? ` (${stats.newAws} new)` : ""}</Link>
        </div>
        <div className="admin-stat-card admin-stat-card--accent">
          <span>New Applications</span>
          <strong>{stats.newApplications}</strong>
          <Link to="/admin/applications">View new</Link>
        </div>
      </div>

      <div className="admin-dash-grid">
        <section className="admin-panel">
          <div className="admin-panel-head">
            <h2>Recent blogs</h2>
            <Link to="/admin/blogs">All blogs</Link>
          </div>
          <ul className="admin-simple-list">
            {recentBlogs.map((b) => (
              <li key={b.id}>
                <strong>{b.title}</strong>
                <span>{b.publishedAt}</span>
              </li>
            ))}
          </ul>
        </section>
        <section className="admin-panel">
          <div className="admin-panel-head">
            <h2>Open roles</h2>
            <Link to="/admin/jobs">All jobs</Link>
          </div>
          <ul className="admin-simple-list">
            {recentJobs.map((j) => (
              <li key={j.id}>
                <strong>{j.title}</strong>
                <span>{j.location}</span>
              </li>
            ))}
          </ul>
        </section>
        <section className="admin-panel admin-panel--wide">
          <div className="admin-panel-head">
            <h2>Latest contact inquiries</h2>
            <Link to="/admin/contact-inquiries">All inquiries</Link>
          </div>
          {recentContacts.length === 0 ? (
            <p className="admin-empty">No contact inquiries yet.</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Service</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentContacts.map((item) => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{item.email}</td>
                      <td>{item.service || "—"}</td>
                      <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <section className="admin-panel admin-panel--wide">
          <div className="admin-panel-head">
            <h2>Latest applications</h2>
            <Link to="/admin/applications">All applications</Link>
          </div>
          {recentApps.length === 0 ? (
            <p className="admin-empty">No applications yet.</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentApps.map((a) => (
                    <tr key={a.id}>
                      <td>{a.name}</td>
                      <td>{a.jobTitle}</td>
                      <td><span className={`admin-pill admin-pill--${a.status}`}>{a.status}</span></td>
                      <td>{new Date(a.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
